import { Flame, Leaf, Milk, Nut, Sprout, Sun, ArrowRight, UtensilsCrossed } from 'lucide-react';
import { Link } from '@/router';
import { SIGNATURES, SPICES } from '@/data/site';
import { GonjongSilhouette, PaluangStack, SongketDivider } from '@/components/Motifs';

const spiceIcons = { flame: Flame, leaf: Leaf, sprout: Sprout, milk: Milk, sun: Sun, nut: Nut };

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <GonjongSilhouette className="pointer-events-none absolute -bottom-1 left-1/2 h-28 w-[560px] -translate-x-1/2 text-turmeric opacity-[0.07]" />
        <div className="mx-auto grid max-w-6xl gap-12 px-4 pb-20 pt-14 sm:px-6 md:pt-20 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <p className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-turmeric/40 bg-turmeric-soft px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-crimson" data-testid="hero-eyebrow">
              Rumah Makan Masakan Padang
            </p>
            <h1 className="animate-fade-up stagger-1 mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
              Cita Rasa Minangkabau,{' '}
              <em className="text-crimson">Dihidangkan</em> Sepenuh Hati
            </h1>
            <p className="animate-fade-up stagger-2 mt-6 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg">
              Sepiring nasi hangat, rendang yang dimasak perlahan, gulai berkuah santan keemasan,
              dan sambal ijo yang menggigit. Selamat datang di Nasi Padang Sederhana — tempat
              tradisi hidang khas Minang dijaga di setiap meja.
            </p>
            <div className="animate-fade-up stagger-3 mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/menu/"
                data-testid="hero-cta-menu"
                className="group inline-flex items-center gap-2 rounded-full bg-crimson px-7 py-3.5 text-sm font-bold text-santan shadow-md transition-colors duration-200 hover:bg-crimson-dark"
              >
                Lihat Daftar Hidangan
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
              <Link
                to="/tentang/"
                data-testid="hero-cta-tentang"
                className="inline-flex items-center gap-2 rounded-full border-2 border-charcoal/15 px-7 py-3 text-sm font-bold text-charcoal transition-colors duration-200 hover:border-turmeric hover:text-crimson"
              >
                Mengenal Tradisi Hidang
              </Link>
            </div>
            <ul className="animate-fade-up stagger-4 mt-10 flex flex-wrap gap-2" aria-label="Hidangan andalan">
              {['Rendang', 'Ayam Pop', 'Dendeng Balado', 'Gulai', 'Sambal Ijo'].map((chip) => (
                <li key={chip} className="rounded-full border border-stone-300 bg-white px-4 py-1.5 text-xs font-semibold text-stone-600">
                  {chip}
                </li>
              ))}
            </ul>
          </div>

          <div className="animate-fade-up stagger-2 relative lg:col-span-6">
            <div className="absolute -right-3 -top-3 h-full w-full rounded-3xl border-2 border-turmeric/50" aria-hidden="true" />
            <figure className="relative overflow-hidden rounded-3xl shadow-xl">
              <img
                src="/images/hero.jpg"
                alt="Sepiring nasi Padang di atas daun pisang dengan rendang, ayam balado, dan telur"
                className="aspect-[4/3] w-full object-cover"
                fetchPriority="high"
                data-testid="hero-image"
              />
              <figcaption className="absolute bottom-0 left-0 right-0 bg-charcoal/85 px-5 py-3 text-xs font-medium tracking-wide text-santan backdrop-blur-sm">
                Nasi hangat, lauk berlimpah, sambal di ulekan — cara Minang menyambut tamu.
              </figcaption>
            </figure>
            <div className="animate-drift absolute -left-4 top-8 hidden rounded-2xl border border-stone-200 bg-white px-4 py-3 shadow-lg sm:block" data-testid="hero-badge">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-turmeric">Dimasak Perlahan</p>
              <p className="font-display text-lg font-bold text-charcoal">Santan &amp; Rempah Asli</p>
            </div>
          </div>
        </div>
        <SongketDivider />
      </section>

      {/* HIDANGAN KHAS */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 md:py-28" data-testid="signature-section">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-turmeric">Dari Dapur Kami</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl">
              Hidangan Khas yang Selalu Dinanti
            </h2>
          </div>
          <Link to="/menu/" data-testid="signature-cta-menu" className="group inline-flex items-center gap-2 text-sm font-bold text-crimson">
            Lihat semua hidangan
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SIGNATURES.map((s, i) => (
            <article
              key={s.slug}
              data-testid={`dish-card-${s.slug}`}
              className={`group overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm transition-shadow duration-300 hover:shadow-md ${
                i === 0 ? 'sm:col-span-2 sm:row-span-2' : ''
              }`}
            >
              <div className={`overflow-hidden ${i === 0 ? 'aspect-[16/11]' : 'aspect-[4/3]'}`}>
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl font-bold text-charcoal">{s.name}</h3>
                <p className="mt-1 text-sm leading-relaxed text-stone-600">{s.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* TRADISI HIDANG */}
      <section className="bg-charcoal text-stone-200 texture-grain" data-testid="hidang-section">
        <div className="mx-auto grid max-w-6xl items-center gap-14 px-4 py-20 sm:px-6 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-turmeric">Tradisi di Meja Makan</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-santan sm:text-4xl">
              Seni Penyajian <em className="text-turmeric">Hidang</em>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-stone-300">
              Di rumah makan Padang, Anda tidak menunggu pesanan datang satu per satu. Puluhan
              piring kecil berisi aneka lauk dihidangkan sekaligus ke meja — dan Anda cukup
              membayar hidangan yang benar-benar disantap.
            </p>
            <ol className="mt-10 space-y-8">
              {[
                { n: '01', t: 'Semua Dihidangkan Sekaligus', d: 'Piring-piring kecil berisi rendang, gulai, ayam, dan sambal memenuhi meja dalam sekejap.' },
                { n: '02', t: 'Bayar yang Dinikmati Saja', d: 'Ambil lauk yang Anda inginkan. Piring yang tidak disentuh tidak akan dihitung.' },
                { n: '03', t: 'Sambal & Kuah Sesuai Selera', d: 'Tambahkan sambal ijo atau siraman kuah gulai sepuasnya — rasa ada di tangan Anda.' },
              ].map((s) => (
                <li key={s.n} className="flex gap-5" data-testid={`hidang-step-${s.n}`}>
                  <span className="font-display text-3xl font-bold text-turmeric/70">{s.n}</span>
                  <div>
                    <h3 className="text-lg font-bold text-santan">{s.t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-stone-400">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="relative lg:col-span-7">
            <PaluangStack className="pointer-events-none absolute -left-6 -top-10 h-48 w-44 text-turmeric opacity-60" />
            <figure className="relative ml-auto overflow-hidden rounded-3xl shadow-2xl lg:max-w-lg">
              <img
                src="/images/feast.jpg"
                alt="Rangkaian hidangan masakan Padang tersaji di meja makan"
                loading="lazy"
                decoding="async"
                className="aspect-[4/5] w-full object-cover sm:aspect-[4/3] lg:aspect-[4/5]"
              />
            </figure>
          </div>
        </div>
      </section>

      {/* REMPAH */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 md:py-28" data-testid="spice-section">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-turmeric">Rahasia Dapur</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl">
              Rempah Nusantara, Nyawa Setiap Masakan
            </h2>
            <p className="mt-6 text-base leading-relaxed text-stone-600">
              Masakan Minangkabau dibangun di atas bumbu dasar yang diulek dan dimasak perlahan.
              Enam bahan inilah yang membentuk karakter rasa di setiap piring.
            </p>
            <figure className="mt-8 overflow-hidden rounded-3xl shadow-md">
              <img
                src="/images/spices.jpg"
                alt="Aneka rempah dan cabai segar di pasar tradisional"
                loading="lazy"
                decoding="async"
                className="aspect-[16/9] w-full object-cover"
              />
            </figure>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
            {SPICES.map((sp) => {
              const Icon = spiceIcons[sp.icon] || UtensilsCrossed;
              return (
                <li
                  key={sp.name}
                  data-testid={`spice-${sp.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex items-start gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm transition-shadow duration-300 hover:shadow-md"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-turmeric-soft text-crimson">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-charcoal">{sp.name}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-stone-600">{sp.desc}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-crimson text-santan" data-testid="home-cta-section">
        <SongketDivider light />
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-8 px-4 py-16 sm:px-6 md:flex-row md:items-center md:justify-between md:py-20">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Perut Sudah Mulai Bersuara?</h2>
            <p className="mt-3 max-w-lg text-base leading-relaxed text-santan/85">
              Jelajahi daftar hidangan lengkap kami — dari rendang hingga kerupuk kulit — dan
              susun piring Padang impian Anda.
            </p>
          </div>
          <Link
            to="/menu/"
            data-testid="home-cta-menu-bottom"
            className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-santan px-8 py-4 text-sm font-bold text-crimson shadow-lg transition-colors duration-200 hover:bg-turmeric-soft"
          >
            Buka Daftar Hidangan
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
}
